import { useEffect, useState } from 'react';
import { CROP_IDS, CROP_LABELS, type KcRow, type CropId } from './farmTypes';

export default function KcTable() {
  const [rows, setRows] = useState<KcRow[]>([]);
  const [defaults, setDefaults] = useState<KcRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch('/api/admin/kc');
        if (r.status === 403) {
          setError('Not signed in — reload the page to sign in with Cloudflare Access.');
          return;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = (await r.json()) as { rows?: KcRow[]; defaults?: KcRow[] };
        if (cancelled) return;
        setRows(body.rows ?? []);
        setDefaults(body.defaults ?? []);
      } catch (err) {
        if (!cancelled) setError(String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    setSavedAt(null);
    try {
      const r = await fetch('/api/admin/kc', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      if (!r.ok) throw new Error(j.error ?? `HTTP ${r.status}`);
      setSavedAt(Date.now());
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  function addRow() {
    setRows((rs) => [
      ...rs,
      { crop: 'eucalyptus', variety: null, kc: 0.5, source: 'Grower calibration', estimate: true },
    ]);
  }

  function restoreDefaults() {
    setRows(defaults.map((r) => ({ ...r })));
  }

  function updateRow(idx: number, patch: Partial<KcRow>) {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function deleteRow(idx: number) {
    setRows((rs) => rs.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <div className="mb-4">
        <div className="text-sm font-medium mb-1">Kc table</div>
        <div className="text-xs opacity-60">
          ETc = ET₀ × Kc. One value per crop, all year (D-19). A block's Kc override in Farms &amp; blocks
          wins over this table.
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <button onClick={addRow} className="bg-emerald-600 hover:bg-emerald-500 rounded px-3 py-1.5 text-sm">
          + Add row
        </button>
        <button onClick={restoreDefaults} className="bg-slate-800 hover:bg-slate-700 rounded px-3 py-1.5 text-sm">
          Restore defaults
        </button>
        <div className="flex-1" />
        <button
          onClick={save}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-500 rounded px-3 py-1.5 text-sm disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save all'}
        </button>
      </div>

      {loading && <div className="text-sm opacity-60 mb-4">Loading…</div>}

      {error && (
        <div className="mb-4 p-3 rounded bg-rose-900/50 border border-rose-500/40 text-sm">{error}</div>
      )}

      {savedAt && (
        <div className="text-xs opacity-60 mb-4">
          Saved. Block cards use the new Kc on the dashboard's next refresh.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((r, idx) => (
          <div
            key={idx}
            className="bg-slate-900 border border-slate-800 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-6 gap-3 text-sm items-center"
          >
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Crop</div>
              <select
                value={r.crop}
                onChange={(e) => updateRow(idx, { crop: e.target.value as CropId })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              >
                {CROP_IDS.map((c) => (
                  <option key={c} value={c}>
                    {CROP_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Variety</div>
              <input
                value={r.variety ?? ''}
                placeholder="all varieties"
                onChange={(e) => updateRow(idx, { variety: e.target.value || null })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Kc</div>
              <input
                type="number"
                step="0.01"
                value={r.kc}
                onChange={(e) => updateRow(idx, { kc: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block sm:col-span-2">
              <div className="text-xs opacity-60 mb-1">Source</div>
              <input
                value={r.source}
                onChange={(e) => updateRow(idx, { source: e.target.value })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <div className="flex items-center gap-2 justify-between">
              <label className="inline-flex items-center gap-2 text-xs opacity-80">
                <input
                  type="checkbox"
                  checked={r.estimate}
                  onChange={(e) => updateRow(idx, { estimate: e.target.checked })}
                />
                estimate
              </label>
              {r.estimate && (
                <span className="bg-amber-500/20 text-amber-100 border border-amber-400/40 rounded px-1.5 py-0.5 text-[10px]">
                  estimate — calibrate
                </span>
              )}
              <button onClick={() => deleteRow(idx)} className="bg-rose-800 rounded px-2 py-1 text-xs">
                Delete
              </button>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="text-center opacity-60 py-12 text-sm">
            No Kc rows yet. Click "+ Add row" to create one.
          </div>
        )}
      </div>
    </div>
  );
}
